// config/firebase.js
// Firebase Configuration and Initialization
// ============================================
// This file initializes Firebase services for the frontend.
// Auth, Analytics, and any future Firebase services go here.
// ============================================

import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider,
  // FacebookAuthProvider,  // Uncomment when Facebook added
  // OAuthProvider,         // For Apple Sign-In when added
  connectAuthEmulator 
} from 'firebase/auth';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getMessaging, getToken, onMessage, isSupported as isMessagingSupported } from 'firebase/messaging';

// ============================================
// FIREBASE CONFIGURATION
// ============================================
// These values are safe to expose in frontend code.
// Security is enforced by Firebase's authorized domains.
// ============================================

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyD5y7JCGJI1oOS2jM-4FZkuyRhv6KQiOio",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "uniledge-web.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "uniledge-web",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "uniledge-web.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "241564733286",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:241564733286:web:fa8069447a2826b75a42a7",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-JTX0TLCC2X"
};

// ============================================
// INITIALIZE FIREBASE
// ============================================
// Prevent multiple initializations (important for hot reloading)

const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// ============================================
// INITIALIZE FIREBASE AUTH
// ============================================

const auth = getAuth(app);

// Use auth emulator in development (optional - for local testing)
// Uncomment if you want to use Firebase Auth Emulator
// if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_AUTH_EMULATOR === 'true') {
//   connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
// }

// ============================================
// AUTH PROVIDERS
// ============================================

// Google Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account' // Always show account selection
});
// Add scopes if needed
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Facebook Provider (uncomment when ready)
// const facebookProvider = new FacebookAuthProvider();
// facebookProvider.addScope('email');
// facebookProvider.addScope('public_profile');

// Apple Provider (uncomment when ready)
// const appleProvider = new OAuthProvider('apple.com');
// appleProvider.addScope('email');
// appleProvider.addScope('name');

// ============================================
// INITIALIZE ANALYTICS
// ============================================
// Analytics only works in browser environment

let analytics = null;

const initializeAnalytics = async () => {
  if (typeof window !== 'undefined') {
    const supported = await isSupported();
    if (supported) {
      analytics = getAnalytics(app);
    }
  }
  return analytics;
};

// Initialize analytics (non-blocking)
initializeAnalytics().catch(console.warn);


// ============================================
// INITIALIZE FIREBASE CLOUD MESSAGING (FCM)
// ============================================

let messaging = null;

const initializeMessaging = async () => {
  if (typeof window !== 'undefined') {
    const supported = await isMessagingSupported();
    if (supported) {
      messaging = getMessaging(app);
    }
  }
  return messaging;
};

// Initialize messaging (non-blocking)
initializeMessaging().catch(console.warn);

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if denied
 */
const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }
    
    if (!messaging) {
      await initializeMessaging();
    }
    
    if (!messaging) {
      console.warn('FCM not supported in this browser');
      return null;
    }
    
    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BLBz5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5Xh8fJ5'
    });
    
    console.log('FCM Token:', token);
    return token;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Listen for foreground messages
 * @param {function} callback - Called when message received
 * @returns {function} Unsubscribe function
 */
const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.warn('FCM not initialized');
    return () => {};
  }
  
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};



// ============================================
// EXPORTS
// ============================================

export { 
  app, 
  auth, 
  analytics,
  googleProvider,
  // facebookProvider,  // Uncomment when ready
  // appleProvider,     // Uncomment when ready
  requestNotificationPermission,
  onForegroundMessage,
  firebaseConfig 
};

export default app;